import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { TableVirtuoso, TableComponents } from "react-virtuoso";
import {
  getLeads,
  downloadLeadsCSV,
  downloadBuyerListCSV,
  getNoticeLeads,
  downloadNoticeLeadsCSV,
  LeadFilters,
  NoticeRecordType,
} from "../api";
import { Lead, NoticeLead } from "../types";
import { exportErrorMessage, TrialExportLockedError } from "../lib/exportErrors";
import { statusMeta } from "../lib/statusMeta";
import {
  LEAD_SIGNAL_PRIORITY,
  USER_VISIBLE_LEAD_SIGNALS,
  leadTypeMeta,
  computeAvailableSignalTypes,
} from "../lib/leadTypeMeta";
import { capture } from "../lib/analytics";
import { formatCurrency } from "../lib/format";
import BuyBoxBar, { BuyBoxCriteria, EMPTY_CRITERIA } from "./BuyBoxBar";
import { SCOUT_BUY_BOX_LIMIT } from "../lib/planCapabilities";

// delinquencyBadge renders how far behind on property taxes the owner is.
//
// Shown from TWO years, not one. Measured on prod 2026-09-01, 1,799 of the
// 2,985 Wake leads carrying this field are exactly one year behind -- a badge on
// 59% of rows is decoration, not signal. Two-plus starts separating leads, and
// five-plus gets the stronger treatment: those are the owners a tax foreclosure
// realistically reaches.
//
// Returns null (not an empty fragment) so the caller can test it when deciding
// whether the Flags cell is empty.
export function delinquencyBadge(years: number | null): React.JSX.Element | null {
  if (years === null || years < 2) return null;
  const severe = years >= 5;
  return (
    <span
      className={`lv-flag lv-flag--delinquent${severe ? " lv-flag--delinquent-severe" : ""}`}
      title={`${years} distinct tax years unpaid`}
    >
      {years} yrs behind
    </span>
  );
}

// "scout" = full rows, scoped server-side to the user's chosen counties, with
// Scout's saved-search cap and no statewide buyer-list export. "full" =
// pro/team/admin, statewide. Both see owner, mailing and equity on every row:
// the Scout teaser (blanked fields + 50 unlock credits a month) was retired on
// 2026-10-14. Threaded down from Dashboard's planCapabilities.leadsTier.
export type LeadsTier = "scout" | "full";

interface LeadsViewProps {
  countyId: string;
  tier: LeadsTier;
  /** Notification bell deep link (#2302): saved buy box to apply on arrival. */
  boxRequest?: { id: string; n: number };
}

type SortKey = "owner" | "equity";

// "" = resolved parcel leads + auctions (the /leads superset — real parcels with
// a mailing address, filterable by buy box). A notice record type switches to the
// flat statewide legal-notice track (notice_leads) — the pre-resolution breadth.
type ListType = "" | NoticeRecordType;

const NOTICE_TYPE_OPTIONS: { value: ListType; label: string }[] = [
  { value: "", label: "Leads & auctions" },
  { value: "probate", label: "Notices · Probate" },
  { value: "foreclosure", label: "Notices · Foreclosure" },
  { value: "hoa_foreclosure", label: "Notices · HOA" },
  { value: "divorce", label: "Notices · Divorce" },
  { value: "tax_foreclosure", label: "Notices · Tax foreclosure" },
  { value: "execution_sale", label: "Notices · Execution sale" },
];

// Signal-type -> chip label (property_lead_signals.signal_type).
//
// DERIVED from leadTypeMeta, not hand-listed. The previous hardcoded map held 6
// of the 9 signal types, and the lookup below falls back to the raw key — so
// `code_enforcement`, `condemnation` and `tax_delinquent` rendered in this table
// as literal snake_case. Deriving means a new signal type (execution_sale
// included, #936) is labelled everywhere the moment it is added to the one
// registry (lib/leadTypeMeta.ts).
const SIGNAL_LABELS: Record<string, string> = Object.fromEntries(
  LEAD_SIGNAL_PRIORITY.map((t) => [t, leadTypeMeta(t).label]),
);

// Delegates to lib/format.ts (#1033). NOTE: the pre-refactor version put
// a minus sign directly after "$" for negatives ("$-1,234"), which can
// happen here (an underwater equityEstimate); the shared formatCurrency
// uses Intl's "-$1,234" convention instead. No other observed behavior
// change (null → "—", 0 → "$0").
function money(n: number | null): string {
  return formatCurrency(n, { fallback: "—" });
}

// shortDate intentionally NOT migrated to lib/format.ts's formatShortDate:
// this slices an already-ISO `noticeDate` string directly rather than
// parsing it through `Date`, which is a genuinely different mechanism
// (no timezone conversion, no "Invalid Date" risk) and has exactly one
// call site. Left as-is per #1033's guidance to leave single-use,
// non-equivalent formatters where they are.
function shortDate(s: string | null): string {
  if (!s) return "—";
  return s.slice(0, 10);
}

// formatPropertySummary condenses the physical description (type, beds/
// baths, sqft, year built) into one line for the Property column. Added
// 2026-09-16 (investor-eyes audit): these fields were already fetched for
// the property detail modal but never reached the Leads table at all —
// this is deliberately compact rather than one column per field, since the
// table already carries 10+ columns.
function formatPropertySummary(l: Lead): string {
  const parts: string[] = [];
  if (l.propertyType) parts.push(l.propertyType);
  const beds = l.bedrooms != null ? `${l.bedrooms}bd` : null;
  const baths = l.fullBaths != null ? `${l.fullBaths}ba` : null;
  const bedBath = [beds, baths].filter(Boolean).join("/");
  if (bedBath) parts.push(bedBath);
  if (l.totalSqft != null) parts.push(`${Math.round(l.totalSqft).toLocaleString()} sqft`);
  if (l.yearBuilt != null) parts.push(`Built ${l.yearBuilt}`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

// Maps the buy-box criteria onto the /leads query params. countyId is
// undefined for Scout (tier === "scout") — the server hard-scopes /leads to
// the caller's chosen counties regardless of any countyId/counties param, so
// there's no reason to narrow the query with one; passing the active map
// county for Scout would just look like a filter that isn't actually applied.
function criteriaFilters(countyId: string | undefined, c: BuyBoxCriteria, limit: number): LeadFilters {
  return {
    countyId,
    signalTypes: c.signalTypes,
    minValue: c.minValue,
    maxValue: c.maxValue,
    minEquity: c.minEquity,
    absentee: c.absentee,
    entity: c.entity,
    heir: c.heir,
    highConfidenceOnly: c.highConfidenceOnly,
    minSqft: c.minSqft,
    maxSqft: c.maxSqft,
    minYearBuilt: c.minYearBuilt,
    maxYearBuilt: c.maxYearBuilt,
    minBeds: c.minBeds,
    minBaths: c.minBaths,
    includeUnknownBuilding: c.includeUnknownBuilding,
    minAcres: c.minAcres,
    maxAcres: c.maxAcres,
    minAmountDue: c.minAmountDue,
    maxAmountDue: c.maxAmountDue,
    minLandValue: c.minLandValue,
    maxLandValue: c.maxLandValue,
    limit,
  };
}

// LeadsView — the "leads + auctions" table. Every tier that reaches it gets the
// same full rows; Scout's are confined to its chosen counties by the server
// (see criteriaFilters). The default view is the resolved-parcel superset (/leads): every property carrying a
// motivated-seller signal, plus the ones already in an active auction,
// filterable by the user's buy box (signal type, value, equity, absentee/
// entity). The record-type selector switches to the flat statewide
// legal-notice track (notice_leads) — the pre-resolution breadth that hasn't
// been matched to a parcel yet. Notice leads are county-scoped for Scout the
// same way, server-side: the request below asks for the whole state and the
// server narrows it to the user's chosen counties.
// Cells for one lead row. Memoised on the lead object: `displayed` is rebuilt
// on sort/filter, but the Lead objects inside it keep their identity, so
// Virtuoso scrolling and re-sorting do not re-render rows that did not change.
const LeadRowCells = React.memo(function LeadRowCells({ l }: { l: Lead }) {
  const sc = statusMeta(l.status).color;
  return (
    <>
              <td className="lv-owner">
                <span className="lv-card-label">Owner</span>
                {l.ownerName || "—"}
              </td>
              <td className="lv-mono">
                <span className="lv-card-label">Mailing</span>
                {l.mailingAddress || "—"}
                {l.mailingCity ? ` ${l.mailingCity}${l.mailingState ? `, ${l.mailingState}` : ""} ${l.mailingZip || ""}` : ""}
              </td>
              <td className="lv-mono"><span className="lv-card-label">Situs</span>{l.situsAddress || l.parcelId}</td>
              <td className="lv-mono">
                <span className="lv-card-label">Property</span>
                {formatPropertySummary(l)}
              </td>
              <td className="lv-mono">
                <span className="lv-card-label">Case</span>
                {l.courtFileNumber || "—"}
                {l.caseStatus ? ` · ${l.caseStatus}` : ""}
              </td>
              <td className="lv-mono lv-num">
                <span className="lv-card-label">Assessed</span>
                {money(l.assessedValue)}
                {l.lastSalePrice != null && (
                  <div className="lv-sub">Last sale {money(l.lastSalePrice)}{l.lastSaleDate ? ` (${shortDate(l.lastSaleDate)})` : ""}</div>
                )}
              </td>
              <td>
                <span className="lv-card-label">Signals</span>
                <span className="lv-flags">
                  {/* #2140 — active foreclosure status + code-violation-family
                      signal on the same property. Shown first: this is the
                      strongest lead this table can produce, and it should be
                      the first thing a scanning eye hits, not buried after the
                      generic signal chips. */}
                  {l.highConfidenceOverlap && (
                    <span
                      className="lv-flag lv-flag--highconf"
                      title="Active foreclosure status AND a code-violation/condemnation signal on this property"
                    >
                      High-Confidence
                    </span>
                  )}
                  {l.inAuction && <span className="lv-flag lv-flag--auction">Auction</span>}
                  {(l.signalTypes || []).map((s) => (
                    <span key={s} className="lv-flag lv-flag--signal">{SIGNAL_LABELS[s] || s}</span>
                  ))}
                  {!l.inAuction && (l.signalTypes || []).length === 0 && <span className="lv-flag-none">—</span>}
                </span>
              </td>
              <td className="lv-mono lv-num lv-equity">
                <span className="lv-card-label">Est. equity</span>
                {money(l.equityEstimate)}
              </td>
              <td>
                <span className="lv-card-label">Status</span>
                <span className="lv-status-pill" style={{ color: sc, backgroundColor: `${sc}14`, borderColor: `${sc}40` }}>
                  {statusMeta(l.status).short}
                </span>
              </td>
              <td>
                <span className="lv-card-label">Flags</span>
                <span className="lv-flags">
                  {l.isAbsentee && <span className="lv-flag lv-flag--absentee">Absentee</span>}
                  {l.isEntityOwner && <span className="lv-flag lv-flag--entity">LLC</span>}
                  {l.isHeirProperty && <span className="lv-flag lv-flag--heir">Estate</span>}
                  {delinquencyBadge(l.yearsDelinquent)}
                  {!l.isAbsentee && !l.isEntityOwner && !l.isHeirProperty &&
                    !delinquencyBadge(l.yearsDelinquent) && (
                    <span className="lv-flag-none">—</span>
                  )}
                </span>
              </td>
    </>
  );
});

// Fixed viewport for the virtualised table. Virtuoso needs a bounded height to
// know which rows are visible; the page scrolls around it.
const LEADS_TABLE_HEIGHT = "min(72vh, 760px)";
const INITIAL_ROWS = 30;

// Defined at module scope so Virtuoso gets stable component identities;
// inline objects would remount every row on each LeadsView render.
const LEADS_TABLE_COMPONENTS: TableComponents<Lead> = {
  Table: (props) => <table {...props} className="lv-table" />,
  TableRow: (props) => <tr {...props} className="lv-row" />,
};

const LeadsView: React.FC<LeadsViewProps> = ({ countyId, tier, boxRequest }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [noticeLeads, setNoticeLeads] = useState<NoticeLead[]>([]);
  const [listType, setListType] = useState<ListType>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // "Only new since my last export" (#3210): the instant of the last CSV export
  // for this county, kept in this browser, so a repeat mailing skips the owners
  // already sent last time.
  const exportedKey = `parcely.leadsExported.${countyId}`;
  const [lastExportedAt, setLastExportedAt] = useLocalStorage<string>(exportedKey, "");
  const [newOnly, setNewOnly] = useState(false);
  const [exportingBuyers, setExportingBuyers] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Buy-box criteria (live) + the debounced copy the fetch actually keys on, so
  // typing in a number field doesn't fire a request per keystroke.
  const [criteria, setCriteria] = useState<BuyBoxCriteria>(EMPTY_CRITERIA);
  const [applied, setApplied] = useState<BuyBoxCriteria>(EMPTY_CRITERIA);

  // Signal types the buy box's checkbox panel should offer (#2257). Derived
  // from `leads` responses, NOT a dedicated fetch: a second concurrent
  // getLeads call here would cost a request for no real benefit. Instead: only UPDATE this from a response whose request had no signal
  // filter applied (`applied.signalTypes.length === 0`) — `applied` starts
  // at EMPTY_CRITERIA, so the very first load for a county is always such a
  // response, giving an accurate county baseline before the user narrows
  // anything. Once a signal filter IS applied, this deliberately holds its
  // last value rather than recomputing from the now-narrower `leads` — the
  // whole point is to not let picking one signal hide the sibling checkboxes.
  const [availableSignalTypes, setAvailableSignalTypes] = useState<string[]>(USER_VISIBLE_LEAD_SIGNALS);

  const noticeMode = listType !== "";
  // Scout is hard-scoped server-side to its chosen counties regardless of any
  // countyId param — see criteriaFilters' comment. Passing undefined here (not
  // the active map county) keeps that honest instead of implying a filter that
  // isn't actually applied.
  const leadsCountyId = tier === "scout" ? undefined : countyId;

  // Funnel: the Leads tab was opened. Once per mount.
  useEffect(() => {
    capture("leads_viewed", { county_id: countyId, tier });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce criteria -> applied.
  useEffect(() => {
    const t = setTimeout(() => setApplied(criteria), 350);
    return () => clearTimeout(t);
  }, [criteria]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const onErr = (err: any, gate: string) => {
      if (cancelled) return;
      if (err?.response?.status === 403) {
        capture("pro_gate_viewed", { gate });
        setError("Leads are on Scout and Pro. Upgrade to unlock owner contacts and exports.");
      } else {
        setError("Couldn't load leads. Please try again.");
      }
    };

    if (noticeMode) {
      // Notice leads are statewide for pro/team/admin; for Scout the server
      // confines the same request to its chosen counties.
      getNoticeLeads({
        state: "NC",
        recordType: listType as NoticeRecordType,
        limit: 1000,
        directMailOnly: true,
      })
        .then((resp) => {
          if (!cancelled) setNoticeLeads(resp.noticeLeads);
        })
        .catch((err) => onErr(err, "notice_leads"))
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      getLeads(criteriaFilters(leadsCountyId, applied, 1000))
        .then((resp) => {
          if (!cancelled) {
            setLeads(resp.leads);
            // #2257 — piggyback availableSignalTypes off this same response
            // rather than firing a second getLeads call (see its state decl
            // above). Only an UNFILTERED-by-signal response is a trustworthy
            // county baseline; a filtered one is left alone so narrowing by
            // one signal can't hide its sibling checkboxes.
            if (applied.signalTypes.length === 0) {
              setAvailableSignalTypes(
                computeAvailableSignalTypes(resp.leads.map((l) => ({ signalTypes: l.signalTypes })))
              );
            }
          }
        })
        .catch((err) => onErr(err, "leads"))
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [leadsCountyId, listType, noticeMode, applied]);

  // Reset to the full vocabulary the moment the county changes, so a menu
  // narrowed by the PREVIOUS county's data doesn't linger — visibly loading
  // a real value shortly after (once the first unfiltered response for the
  // new county lands, per the block above) beats staying wrong.
  useEffect(() => {
    setAvailableSignalTypes(USER_VISIBLE_LEAD_SIGNALS);
  }, [leadsCountyId]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    // Stamped BEFORE the request: a property ingested while the file is being
    // built lands in the next "new" export instead of falling in the gap.
    const startedAt = new Date().toISOString();
    try {
      const blob = noticeMode
        ? await downloadNoticeLeadsCSV({
            state: "NC",
            recordType: listType as NoticeRecordType,
            limit: 2000,
            directMailOnly: true,
          })
        : await downloadLeadsCSV({
            ...criteriaFilters(leadsCountyId, applied, 2000),
            ...(newOnly && lastExportedAt ? { createdAfter: lastExportedAt } : {}),
          });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = noticeMode ? `parcely-${listType}-${stamp}.csv` : `parcely-leads-${countyId}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      capture("export_clicked", {
        type: noticeMode ? `notice_${listType}` : "leads",
        county_id: countyId,
        new_only: !noticeMode && newOnly && !!lastExportedAt,
      });
      if (!noticeMode) setLastExportedAt(startedAt);
    } catch (err) {
      setError(exportErrorMessage(err, "Export failed. Please try again."));
    } finally {
      setExporting(false);
    }
  }, [countyId, leadsCountyId, noticeMode, listType, applied, newOnly, lastExportedAt, setLastExportedAt]);

  // Buyer-list export (#575) — the deduped active-bidder list for cold
  // outreach. Pro-gated server-side; a 403 surfaces the upgrade prompt the
  // same way the leads export does.
  const handleExportBuyers = useCallback(async () => {
    setExportingBuyers(true);
    try {
      const blob = await downloadBuyerListCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "parcely-buyers.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      if (err instanceof TrialExportLockedError) {
        setError(err.message);
      } else if (err?.response?.status === 403) {
        setError("The buyer list is a Pro feature. Upgrade to export active bidders.");
      } else {
        setError("Buyer-list export failed. Please try again.");
      }
    } finally {
      setExportingBuyers(false);
    }
  }, []);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "equity" ? "desc" : "asc");
    }
  };

  // Signal / absentee / entity filtering is server-side (buy box); this only sorts.
  const displayed = useMemo(() => {
    let rows = leads.slice();
    if (sortKey) {
      rows = rows.slice().sort((a, b) => {
        let cmp = 0;
        if (sortKey === "owner") cmp = (a.ownerName || "").localeCompare(b.ownerName || "");
        else cmp = (a.equityEstimate ?? -Infinity) - (b.equityEstimate ?? -Infinity);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [leads, sortKey, sortDir]);

  const rowCount = noticeMode ? noticeLeads.length : leads.length;
  const emptyView = noticeMode ? noticeLeads.length === 0 : displayed.length === 0;

  return (
    <div className="lv">
      <div className="lv-head">
        <div>
          <h1 className="lv-title">Leads &amp; auctions</h1>
          <p className="lv-sub">
            Every motivated-seller signal in NC — filter by your buy box, reach owners by mail.
          </p>
        </div>
        <span className="lv-count-note">
          {rowCount} {noticeMode ? "notice" : rowCount === 1 ? "lead" : "leads"} · refreshed twice daily
        </span>
      </div>

      <div className="lv-filters">
        <select
          className="lv-chip"
          value={listType}
          onChange={(e) => setListType(e.target.value as ListType)}
          title="Choose a lead type"
          aria-label="Lead type"
        >
          {NOTICE_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <button
          className="lv-export"
          onClick={handleExport}
          disabled={exporting || rowCount === 0}
          title="Download these leads as a CSV for direct mail"
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M7 1.5v7M4 5.5L7 8.5l3-3M2.5 11.5h9" />
          </svg>
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
        {!noticeMode && lastExportedAt && (
          <label
            className="lv-export-new"
            title="Skip properties that were already in your last export for this county"
          >
            <input type="checkbox" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} />
            <span>Only new since {new Date(lastExportedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </label>
        )}
        {/* The buyer list is statewide, so it is Pro/team/admin only: the
            server 403s Scout. Hidden rather than offered and refused. */}
        {!noticeMode && tier === "full" && (
          <button
            className="lv-export"
            onClick={handleExportBuyers}
            disabled={exportingBuyers}
            title="Active bidders for cold outreach — deduped buyer list (CSV)"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M7 1.5v7M4 5.5L7 8.5l3-3M2.5 11.5h9" />
            </svg>
            {exportingBuyers ? "Exporting…" : "Export buyer list"}
          </button>
        )}
      </div>

      {/* Buy box — signal / value / equity / owner filters + saved boxes. Only on
          the resolved-parcel leads track (notice leads lack value/equity). */}
      {!noticeMode && (
        <BuyBoxBar
          criteria={criteria}
          onChange={setCriteria}
          availableSignalTypes={availableSignalTypes}
          boxRequest={boxRequest}
          savedLimit={tier === "scout" ? SCOUT_BUY_BOX_LIMIT : undefined}
          saveCountyId={leadsCountyId}
        />
      )}

      {loading && <div className="lv-status">Loading leads…</div>}

      {!loading && error && <div className="lv-status lv-status--error">{error}</div>}

      {!loading && !error && emptyView && (
        <div className="empty-state">
          <p className="empty-state-title">No leads match this view yet</p>
          <p className="empty-state-desc">
            {noticeMode
              ? "Notice leads appear once the statewide legal-notice sync runs (refreshed twice daily)."
              : "No parcels match this buy box. Loosen a filter, or check back — leads refresh twice daily."}
          </p>
        </div>
      )}

      {/* Resolved-parcel leads + auctions (default view). */}
      {!loading && !error && !noticeMode && displayed.length > 0 && (
        <>
          <div className="lv-table-wrap">
            <TableVirtuoso<Lead>
              className="lv-virtuoso"
              style={{ height: LEADS_TABLE_HEIGHT }}
              data={displayed}
              components={LEADS_TABLE_COMPONENTS}
              computeItemKey={(_index, l) => l.propertyId}
              overscan={400}
              // Renders the first rows without measuring so the table (and
              // jsdom, which has no layout) is never empty on first paint.
              initialItemCount={Math.min(displayed.length, INITIAL_ROWS)}
              fixedHeaderContent={() => (
                  <tr>
                    <th className="lv-sortable" onClick={() => toggleSort("owner")}>
                      Owner <span className="lv-sort-ind">↕</span>
                    </th>
                    <th>Mailing address</th>
                    <th>Situs address</th>
                    <th>Property</th>
                    <th>Case</th>
                    <th className="lv-num">Assessed</th>
                    <th>Signals</th>
                    <th className="lv-num lv-sortable" onClick={() => toggleSort("equity")}>
                      Est. equity <span className="lv-sort-ind">↕</span>
                    </th>
                    <th>Status</th>
                    <th>Flags</th>
                  </tr>
              )}
              itemContent={(_index, l) => <LeadRowCells l={l} />}
            />
          </div>
          <div className="lv-note">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
              <circle cx="7" cy="7" r="6" />
              <path d="M7 4.2v.2M7 6.2v3.6" />
            </svg>
            Contact today is by mail (owner + mailing address). Owner data is richest in Mecklenburg, Cabarrus, and Rowan — other counties may have gaps.
          </div>
        </>
      )}

      {/* Statewide legal-notice leads (the pre-resolution breadth track). */}
      {!loading && !error && noticeMode && noticeLeads.length > 0 && (
        <>
          <div className="lv-table-wrap">
            <table className="lv-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>County</th>
                  <th>Mailing / executor</th>
                  <th>Situs address</th>
                  <th>Court file</th>
                  <th>Notice date</th>
                </tr>
              </thead>
              <tbody>
                {noticeLeads.map((n) => (
                  <tr className="lv-row" key={n.id}>
                    <td className="lv-owner">
                      <span className="lv-card-label">Owner</span>
                      {n.ownerName || "—"}
                      {/* #950 — inline rather than its own column: only ~3% of
                          notice_leads carry the flag, so a Flags column would be
                          empty on almost every row. */}
                      {n.isHeirProperty && <span className="lv-flag lv-flag--heir lv-flag--inline">Estate</span>}
                    </td>
                    <td><span className="lv-card-label">County</span>{n.countyName || "—"}</td>
                    <td className="lv-mono">
                      <span className="lv-card-label">Mailing</span>
                      {n.party2Name ? `${n.party2Name} · ` : ""}
                      {n.mailingAddress || "—"}
                      {n.mailingCity ? ` ${n.mailingCity}${n.mailingState ? `, ${n.mailingState}` : ""} ${n.mailingZip || ""}` : ""}
                    </td>
                    <td className="lv-mono">
                      <span className="lv-card-label">Situs</span>
                      {n.situsAddress || "—"}
                      {n.situsCity ? ` ${n.situsCity} ${n.situsZip || ""}` : ""}
                    </td>
                    <td className="lv-mono"><span className="lv-card-label">Court file</span>{n.courtFileNumber || "—"}</td>
                    <td className="lv-mono"><span className="lv-card-label">Notice date</span>{shortDate(n.noticeDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="lv-note">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
              <circle cx="7" cy="7" r="6" />
              <path d="M7 4.2v.2M7 6.2v3.6" />
            </svg>
            Statewide legal notices across NC counties. These are pre-resolution — owner/value enrichment is added once a
            notice address resolves to a parcel, promoting it into the Leads &amp; auctions track.
          </div>
        </>
      )}
    </div>
  );
};

export default LeadsView;
